import {FaEraser, FaSearch} from "react-icons/fa";
import Button from "../../../../components/button/button";
import { Card } from "../../../../components/card/card";
import InputForm from "../../../../components/input/input-form";
import { Template } from "../../../../components/template/template";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AgGridUtils from "../../../../utils/aggrid-utils";
import useNotificationSSE from "./hooks/use-notification-sse";
import { useSearchParams } from "react-router";
import ProjectUtils from "../../../../utils/project-utils";
import NotificationsService from "../../../../services/web/notification-service";
import { toast } from "react-toastify";
import NotificationDetails from "../components/notification-details";
import { useQueryClient } from "@tanstack/react-query";
import { NOTIFICATION_COUNT_QUERY_KEY } from "../../../../hooks/use-keep-session";

export default function NotificationOpen() {
    const [searchParams] = useSearchParams();
    const [searchInput, setSearchInput] = useState("");
    const [selected, setSelected] = useState(new Map());
    const [isLoading, setIsLoading] = useState(false);
    const [config, setConfig] = useState(null);
    const first = useRef(true);
    const gridRef = useRef();
    const { notifications } = useNotificationSSE();
    const queryClient = useQueryClient();

    useEffect(() => {
        let alive = true;

        (async () => {
            const el = document.getElementById("root");
            const mapsKeyFromDom = (el?.dataset?.mapsKey ?? "").trim();

            try {
            const data = await NotificationsService.getConfig();
            if (!alive) return;

            setConfig({
                mapsKey: mapsKeyFromDom || (data?.mapsKey ?? ""),
                hasDispatch: !!data?.hasDispatch,
            });
            } catch (err) {
            console.error(err);
            if (!alive) return;

            setConfig({
                mapsKey: mapsKeyFromDom,
                hasDispatch: false,
            });
            }
        })();

        return () => {
            alive = false;
        };
    }, []);

    const columnDefs = useMemo(() => [
        { 
            headerName: "D/H",
            headerTooltip: "Data/hora", 
            field: "dtEvent", 
            sort: "desc", 
            flex: 1,
            tooltipValueGetter: (params) =>
                params.value
                    ? ProjectUtils.formatDate(params.value, "DD/MM/YYYY hh:mm:ss")
                    : "", 
            valueFormatter: (params) =>
                params.value
                    ? ProjectUtils.formatDate(params.value, "DD/MM/YYYY hh:mm:ss")
                    : "",
            cellStyle: (params) => (params.data?.delayed ? { color: "#ff5151" } : null),
         },
        { headerName: "Notificação", headerTooltip: "Notificação" , field: "title", tooltipField: "title" ,flex: 1, }, 
    ], []);

    const defaultColDef = useMemo(() => ({
        resizable: false,
        sortable: true,
    }), []);

    const selectionColumnDef = useMemo(() => ({
        cellStyle: { paddingLeft: 16 },
        headerStyle: { paddingLeft: 16 },
    }), []);

    const rowSelection = useMemo(() => ({
        mode: "multiRow",
        enableSelectionWithoutKeys: true,
    }), []);

    const applyQuickFilter = useCallback((value) => {
        gridRef.current?.api?.setGridOption("quickFilterText", value);
    }, []);

    const onKeyDown = ({ key }) => {
        if (key === "Enter") { applyQuickFilter(searchInput) };
        if (key === "Escape") handleClear();
    };

    const onCellClicked = useCallback(({ data, node, colDef }) => {
        if (!data || !node) return;
        if (colDef?.colId === "ag-Grid-SelectionColumn") return;

        const api = gridRef.current?.api;
        if (!api) return;

        setSelected((prev) => {
            const onlySelected = prev.size === 1 && prev.has(data.id);

            api.deselectAll();
            if (onlySelected) return new Map();

            node.setSelected(true);
            return new Map([[data.id, data]]);
        });
    }, []);

    const onSelectionChanged = useCallback(() => {
        const rows = gridRef.current?.api?.getSelectedRows?.() ?? [];

        setSelected((prev) => {
            if (rows.length === prev.size && rows.every((r) => prev.has(r.id))) {
                return prev;
            }

            return new Map(rows.map((r) => [r.id, prev.get(r.id) ?? r]));
        });
    }, []);

    useEffect(() => {
        queryClient.setQueryData(NOTIFICATION_COUNT_QUERY_KEY, {count: notifications.length, newEvent: false});

        const api = gridRef.current?.api;
        if (!api) return;

        if (first.current && searchParams.has("id")) {
            const idParam = searchParams.get("id");
            const find = notifications.find((e) => e.id?.toString() === idParam);

            if (find) {
                api.deselectAll();
                api.forEachNode((node) => node.setSelected(node.data?.id === find.id));
                setSelected(new Map([[find.id, find]]));
                first.current = false;
            };
        };

        setSelected((prev) => {
            if (!prev.size) return prev;

            const idsExistentes = new Set(notifications.map((n) => n.id));
            const toRemove = [];

            for (const [id] of prev) {
                if (!idsExistentes.has(id)) toRemove.push(id);
            }

            if (!toRemove.length) return prev;

            const m = new Map(prev);
            for (const id of toRemove) m.delete(id);

            api.forEachNode((node) => node.setSelected(m.has(node.data?.id)));

            return m;
        });
    }, [notifications, searchParams, queryClient]);

    const markAsSigned = async () => {
        const ids = [...selected.keys()];
        const formData = new FormData();
        formData.append("id", ids.join(","));
        
        setIsLoading(true);

        try {
            await NotificationsService.markAsSigned(formData);
            toast.success("Assinado com sucesso");
        } catch (error) {
            console.error(error);
            ProjectUtils.swalErrorMessage("", "Não foi possível assinar.", "Ok");
        } finally {
            setIsLoading(false);
        }
    };

    const returnToDefault = () => {
        setSelected(new Map());
        gridRef.current?.api.deselectAll();
    };

    const handleClear = useCallback(() => {
        setSearchInput("");
        applyQuickFilter("");
    }, [applyQuickFilter]);

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Notificações em aberto</Template.Title>
            </Template.Header>
            <Template.Body>
                <div className="grid grid-cols-12 gap-2 items-stretch h-[calc(100vh-69px)]">
                    <div className="col-span-4 flex flex-col gap-2 h-full min-h-0">
                        <Card className={"flex-none"}>
                            <Card.Body>
                                <div className="flex gap-4 items-end">
                                    <div className="flex-1">
                                        <InputForm
                                            id="searchInput"
                                            label="Pesquisa rápida"
                                            value={searchInput}
                                            onChange={(e) => setSearchInput(e.target.value)}
                                            onKeyDown={onKeyDown}
                                        />
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            target="primary-outline"
                                            title="Limpar"
                                            onClick={handleClear}
                                        >
                                            <FaEraser />
                                        </Button>
                                        <Button 
                                            title="Buscar"
                                            onClick={() => applyQuickFilter(searchInput)}
                                        >
                                            <FaSearch />
                                        </Button>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                        <Card className={"flex-1 min-h-0"}>
                            <Card.Body className={"h-full min-h-0"}>
                                <div className="flex flex-col h-full min-h-0">
                                    <div 
                                        className="flex-1 min-h-0 rounded-t-lg border border-zinc-200"
                                        style={{
                                            "--ag-wrapper-border": "none",
                                            "--ag-wrapper-radius": "0px",
                                            "--ag-border-radius": "0px",
                                        }}
                                    >
                                        <AgGridReact
                                            ref={gridRef}
                                            rowData={notifications}
                                            getRowId={({ data }) => String(data.id)}
                                            columnDefs={columnDefs}
                                            defaultColDef={defaultColDef}
                                            selectionColumnDef={selectionColumnDef}
                                            suppressCellFocus={true}
                                            animateRows={false}
                                            rowHeight={32}
                                            rowSelection={rowSelection}
                                            onCellClicked={onCellClicked}
                                            onSelectionChanged={onSelectionChanged}
                                            onFilterChanged={AgGridUtils.filterChangedNoRowsOverlay}
                                            gridOptions={{localeText: AgGridUtils.localization()}}
                                            theme={AgGridUtils.generalTheme()}
                                            enableBrowserTooltips={true}
                                        />
                                    </div>
                                    <div className={"flex flex-none items-center gap-3 px-3 py-2 min-h-12.5 border border-t-0 rounded-b-lg border-zinc-200"}>
                                        {selected?.size > 1 && (
                                            <Button
                                                onClick={markAsSigned}
                                                actionFeedbackType="save"
                                                loadingText="Assinando..."
                                                successText="Assinado com sucesso"
                                            >
                                                Assinar notificações selecionadas ({selected.size})
                                            </Button>
                                        )}
                                        <div className="text-sm font-bold ml-auto">Total: {notifications.length}</div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-span-8 flex min-w-0 h-full min-h-0">
                        <Card className={"w-full h-full min-h-0"}>
                            <Card.Body className="h-full min-h-0 p-0">
                                <div className="h-full min-h-0 relative">
                                    <div 
                                        hidden={ selected?.size === 1 } 
                                        className="h-full flex items-center justify-center p-4 select-none pointer-events-none opacity-50"
                                    >
                                        <img 
                                            src={NotificationsService.corporationImage}
                                            className="max-h-full max-w-full object-contain"
                                        />
                                    </div>
                                    {selected?.size === 1 && (
                                        <div className="h-full min-h-0 p-0 overflow-hidden">
                                            <div className="h-full min-h-0 overflow-y-auto">
                                                <NotificationDetails 
                                                    selected={selected.size === 1 ? selected.values().next().value : undefined}
                                                    mapsKey={config.mapsKey}
                                                    returnToDefault={returnToDefault}
                                                    target={"open"}
                                                    setIsLoading={setIsLoading}
                                                    hasDispatch={config.hasDispatch}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                </div>
                
            </Template.Body>
        </Template>
    )
}